import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
} from "react";
import {
  ApiDataResponse,
  ApiResponse,
  Student,
} from "../services/Dtos";
import { useUserContext } from "./userContext";
import useAxios from "../hooks/useAxios";
import { useGlobalError } from "./GlobalErrorContext";

interface StudentsContextType {
  students: Student[];
  setStudents: React.Dispatch<React.SetStateAction<Student[]>>;
  error: string | null;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  selectedStudent: Student | null;
  setSelectedStudent: React.Dispatch<React.SetStateAction<Student | null>>;
  addStudent: (newStudent: Student) => Promise<Student | null>;
  editStudent: (
    studentToEdit: Student,
    studentId: string
  ) => Promise<Student | null>;
  deleteStudent: (studentId: string) => Promise<ApiResponse | null>;
  fetchStudents: () => void;
}

interface Props {
  children: ReactNode;
}

const StudentsContext = createContext<StudentsContextType | undefined>(
  undefined
);

export const StudentsProvider = ({ children }: Props) => {
  const axiosClient = useAxios();
  const [students, setStudents] = useState<Student[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isLoading: userLoading } = useUserContext();
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const { setError: setGlobalError } = useGlobalError();

  const fetchStudents = async () => {
    setIsLoading(true);
    try {
      setError(null);
      const response = await axiosClient.get<ApiDataResponse<Student[]>>(
        "students"
      );
      if (response.data?.succeeded) {
        const students = response.data.data;
       
        setStudents(students);
      } else {
        const errorMessage = `Error fetching students: ${response.data?.message}`;
        setError(errorMessage);
        setGlobalError({
          message: errorMessage,
          onRetry: () => window.location.reload(),
        });
      }
    } catch (error) {
      const errorMessage = "Failed to fetch students";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!user || !token || userLoading) {
      setIsLoading(false);
      return;
    }
    fetchStudents();
  }, [user, userLoading]);

  const addStudent = async (
    newStudent: Student
  ): Promise<Student | null> => {
    try {
      const response = await axiosClient.post<ApiDataResponse<Student>>(
        "students",
        newStudent
      );

      if (response.data?.succeeded) {
        const savedStudent = response.data.data;
        setStudents((prevStudents) => [...prevStudents, savedStudent]);
        return savedStudent;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    } finally {
    }
  };

  //Edit or Update Student
  const editStudent = async (
    studentToEdit: Student,
    studentId: string
  ): Promise<Student | null> => {
    let updatedStudent: Student | null = null;

    try {
      const response = await axiosClient.put<ApiResponse>(
        `students/${studentId}`,
        studentToEdit
      );

      if (response.data?.succeeded) {
        setStudents((prevStudents) => {
          return prevStudents.map((s) => {
            if (s.id === studentId) {
              updatedStudent = {
                ...s,
                ...studentToEdit,
                gender: studentToEdit.gender as number,
                status: studentToEdit.status,
              };
              return updatedStudent;
            }
            return s;
          });
        });

        // Optionally, you can update any other state here using the updatedStudent variable
        if (updatedStudent) {
          // For example, if you want to update the selected student state:
          setSelectedStudent(updatedStudent);
        }

        return updatedStudent;
      } else {
        return updatedStudent;
      }
    } catch (error) {
      return null;
    } finally {
    }
  };

  //DeleteStudent
  const deleteStudent = async (
    studentId: string
  ): Promise<ApiResponse | null> => {
    try {
      const response = await axiosClient.delete<ApiResponse>(
        `students/${studentId}`
      );

      if (response.data?.succeeded) {
        setStudents((prevStudents) =>
          prevStudents.filter((s) => s.id !== studentId)
        );

        if (selectedStudent?.id === studentId) {
          setSelectedStudent(null);
        }

        return response.data;
      } else {
        return response.data;
      }
    } catch (error) {
      return null;
    } finally {
    }
  };

  return (
    <StudentsContext.Provider
      value={{
        students,
        setStudents,
        error,
        isLoading,
        setIsLoading,
        addStudent,
        editStudent,
        selectedStudent,
        setSelectedStudent,
        deleteStudent,
        fetchStudents,
      }}
    >
      {children}
    </StudentsContext.Provider>
  );
};

export const useStudentsContext = () => {
  const context = useContext(StudentsContext);
  if (!context) {
    throw new Error("useStudentsContext must be used within a StudentProvider");
  }
  return context;
};
